import { motion } from "framer-motion";
import { Navbar, FinalCTA, Waveform, fadeUp, staggerContainer, btnMotion } from "../components/Shared";

const EXPERIENCES = [
  {
    role: "Employee",
    title: "Private, on-demand support",
    desc: "Access certified wellness coaching, stress regulation tools, and skill-building directly. No manager approval needed. Your data is yours."
  },
  {
    role: "Manager",
    title: "Confidence in tough conversations",
    desc: "Get micro-training and real-time guidance on how to support a team member showing signs of burnout, without acting as a therapist."
  },
  {
    role: "Organization",
    title: "Visibility without violation",
    desc: "Understand macro-level trends in burnout risk, program adoption, and team health to make proactive, structural changes."
  },
  {
    role: "People Team",
    title: "Seamless setup, proven ROI",
    desc: "Deploy the program in days with zero IT heavy lifting. Track utilization and engagement metrics to easily prove the business impact."
  }
];

const ECOSYSTEM_LAYERS = [
  { 
    level: "Individual Employee", 
    desc: "Private access to stress regulation tools and 1-on-1 wellness coaching." 
  },
  { 
    level: "Manager / Team Lead", 
    desc: "Actionable guides and micro-training to foster psychological safety." 
  },
  { 
    level: "Department / Team", 
    desc: "Anonymous, aggregated health trends and targeted team workshops." 
  },
  { 
    level: "Organization / Leadership", 
    desc: "Macro-level ROI, culture metrics, and proactive retention strategies." 
  }
];

export default function Platform() {
  return (
    <main className="cw-platform">
      <Navbar />
      
      <section className="cw-section" style={{ paddingTop: 'var(--space-xl)', textAlign: 'center' }}>
        <div className="cw-section__inner">
          <motion.div variants={staggerContainer(0.1)} initial="hidden" animate="visible">
            <motion.p className="cw-eyebrow" variants={fadeUp}>The Platform</motion.p>
            <motion.h1 variants={fadeUp} style={{ fontSize: '3rem', marginBottom: 'var(--space-md)' }}>
              Support → Understand → Enable → Improve
            </motion.h1>
            <motion.p variants={fadeUp} style={{ fontSize: '1.2rem', maxWidth: '650px', margin: '0 auto var(--space-md)', opacity: 0.8 }}>
              Cognito connects confidential wellness coaching, stress regulation, and behavioral skill-building into a single, continuous loop of care and insight.
            </motion.p>
            <div style={{ display: 'flex', justifyContent: 'center' }}><Waveform /></div>
          </motion.div>
        </div>
      </section>

      <section className="cw-section cw-section--alt">
        <div className="cw-section__inner">
          <motion.p className="cw-eyebrow" style={{ textAlign: 'center' }}>The Ecosystem</motion.p>
          <motion.h2 className="cw-section__title" style={{ textAlign: 'center', fontSize: '2.2rem', marginBottom: 'var(--space-lg)' }}>
            How the layers connect
          </motion.h2>
          <motion.div 
            variants={staggerContainer(0.1)} 
            initial="hidden" 
            whileInView="visible" 
            viewport={{ once: true }}
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)', alignItems: 'center' }}
          >
            {ECOSYSTEM_LAYERS.map((layer, i) => (
              <motion.div 
                key={layer.level} 
                className="cw-card" 
                variants={fadeUp} 
                style={{ width: '100%', maxWidth: '600px', textAlign: 'center', position: 'relative', padding: 'var(--space-md)' }}
              >
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--cw-pink)', textTransform: 'uppercase' }}>Layer {i + 1}</span>
                <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.4rem', margin: 'var(--space-xs) 0' }}>{layer.level}</h3>
                <p style={{ fontSize: '0.95rem', opacity: 0.8, maxWidth: '450px', margin: '0 auto' }}>{layer.desc}</p>
                {i < 3 && (
                  <div style={{ position: 'absolute', bottom: '-28px', left: '50%', transform: 'translateX(-50%)', color: 'var(--cw-teal)', fontSize: '1.5rem' }}>
                    ↓
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="cw-section">
        <div className="cw-section__inner">
          <motion.p className="cw-eyebrow" variants={fadeUp}>Experiences</motion.p>
          <motion.h2 className="cw-section__title" variants={fadeUp} style={{ fontSize: '2.2rem', marginBottom: 'var(--space-lg)' }}>
            Designed for every level of the company.
          </motion.h2>
          <motion.div variants={staggerContainer(0.1)} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-md)' }}>
            {EXPERIENCES.map((exp) => (
              <motion.div key={exp.role} className="cw-card" variants={fadeUp}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--cw-teal)', textTransform: 'uppercase' }}>{exp.role}</span>
                <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.4rem', margin: 'var(--space-xs) 0' }}>{exp.title}</h3>
                <p style={{ opacity: 0.8, fontSize: '0.95rem' }}>{exp.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="cw-section cw-section--alt" style={{ textAlign: 'center' }}>
        <div className="cw-section__inner">
          <motion.p className="cw-eyebrow" variants={fadeUp}>The Privacy Boundary</motion.p>
          <motion.h2 variants={fadeUp} style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', marginBottom: 'var(--space-sm)' }}>
            Built on absolute confidentiality.
          </motion.h2>
          <motion.div variants={fadeUp} style={{ maxWidth: '650px', margin: '0 auto', opacity: 0.8, fontSize: '1.05rem', lineHeight: '1.6' }}>
            <p style={{ marginBottom: 'var(--space-sm)' }}>
              Individual conversations, session notes, and assessment responses are <strong>strictly confidential</strong> between the employee and their certified wellness coach. 
            </p>
            <p style={{ marginBottom: 'var(--space-sm)', color: 'var(--cw-teal)', fontWeight: '600' }}>
              We do not record sessions. Ever.
            </p>
            <p>
              Leadership dashboards only display anonymized, aggregate data (e.g., "30% of Team X engaged with stress modules this month"). Individual identities and specific issues are never exposed to the organization.
            </p>
          </motion.div>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}